/**
 * The shop's business day.
 *
 * Everything the reports group by — sales, purchases, expenses, the day close —
 * is keyed by one of these rather than by a timestamp, so "today" means what the
 * shopkeeper means by it.
 *
 * Mirror `DayKey` as a `date` column, never a timestamp.
 * `services/bof/apps/common/day.py` is the server-side twin.
 */

/** `YYYY-MM-DD` in the shop's local time. */
export type DayKey = string;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * The local calendar day a moment belongs to.
 *
 * Built from the local getters on purpose. `toISOString().slice(0, 10)` is the
 * obvious one-liner and it is wrong here: Laos is UTC+7, so a sale rung up at
 * 00:30 on the 28th would be filed under the 27th, and every evening sale after
 * 17:00 would land on the next day. That error is invisible in a same-day view
 * and corrupts every daily total.
 */
export function businessDay(d: Date): DayKey {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function todayKey(): DayKey {
  return businessDay(new Date());
}

/** Parses a DayKey back to local midnight. */
export function dayStart(day: DayKey): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function addDays(day: DayKey, n: number): DayKey {
  const d = dayStart(day);
  d.setDate(d.getDate() + n);
  return businessDay(d);
}

/** Inclusive at both ends, which is how the period chips read to a user. */
export interface DayRange {
  from: DayKey;
  to: DayKey;
}

export type RangePreset = 'today' | 'week' | 'month';

export function presetRange(preset: RangePreset, today: DayKey = todayKey()): DayRange {
  if (preset === 'today') return { from: today, to: today };
  if (preset === 'week') return { from: addDays(today, -6), to: today };
  const d = dayStart(today);
  return { from: businessDay(new Date(d.getFullYear(), d.getMonth(), 1)), to: today };
}

/** DayKeys sort correctly as strings, which is the whole reason for the format. */
export function inRange(day: DayKey, range: DayRange): boolean {
  return day >= range.from && day <= range.to;
}
